// File: frontend/src/components/AccessDenied.js
//
// Friendly, calm "no access" screen. Shows a locked card over a purely
// decorative blurred placeholder (CSS shapes only — NO real data is ever
// fetched or rendered for a page the user cannot access).

import React from "react";
import { Box, Typography, Button } from "@mui/material";
import { alpha } from "@mui/material/styles";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "../context/PermissionsContext";

export default function AccessDenied({ title = "You do not have access to this content." }) {
  const navigate = useNavigate();
  const { isAdmin, allowedPages } = usePermissions();
  // Send the user somewhere they CAN go: dashboard if allowed, else their first
  // allowed page (so a user without dashboard access isn't dead-ended here).
  const pages = Array.from(allowedPages || []);
  const target = isAdmin || pages.includes("/dashboard") ? "/dashboard" : (pages[0] || "/login");
  const targetLabel = target === "/dashboard" ? "Dashboard" : (target.replace("/", "") || "sign in");
  return (
    <Box sx={{ position: "relative", minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
      {/* Decorative blurred placeholder — no real/sensitive data. */}
      <Box aria-hidden sx={{ position: "absolute", inset: 0, filter: "blur(9px)", opacity: 0.25, pointerEvents: "none", overflow: "hidden" }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 2, p: 2 }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Box key={i} sx={{ height: 120, borderRadius: 3, bgcolor: "action.hover", border: "1px solid", borderColor: "divider" }} />
          ))}
        </Box>
      </Box>

      <Box sx={{
        position: "relative", textAlign: "center", maxWidth: 440, width: "100%",
        p: { xs: 3, sm: 4 }, borderRadius: 4, bgcolor: "background.paper",
        border: "1px solid", borderColor: "divider", boxShadow: "0 10px 40px rgba(0,0,0,0.18)",
      }}>
        <Box sx={{
          width: 56, height: 56, borderRadius: "50%", mx: "auto", mb: 2,
          display: "grid", placeItems: "center",
          bgcolor: (t) => alpha(t.palette.success.main, 0.12), color: "success.main",
        }}>
          <LockOutlinedIcon sx={{ fontSize: 28 }} />
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: 18, mb: 1 }}>{title}</Typography>
        <Typography sx={{ color: "text.secondary", fontSize: 13.5, lineHeight: 1.6, mb: 0.5 }}>
          Please contact your administrator to grant you access.
        </Typography>
        <Typography sx={{ color: "text.disabled", fontSize: 12, mb: 2.5 }}>
          This page is restricted for your current role.
        </Typography>
        <Button variant="contained" onClick={() => navigate(target)}
          sx={{ borderRadius: 2.5, textTransform: "capitalize", fontWeight: 700, px: 3 }}>
          Go to {targetLabel}
        </Button>
      </Box>
    </Box>
  );
}
